import { Dialog } from '@base-ui/react/dialog';
import { XMarkIcon } from '@heroicons/react/24/outline';
import './GettingStartedDialog.css';

interface GettingStartedDialogProps {
  open: boolean;
  onClose: () => void;
}

/**
 * [Discovery dimension, Discovery weight % (null = Coming Soon, contributes
 * 0% today), Delivery dimension, Delivery weight %].
 * Weights mirror lib/rubrics.ts's DIMENSION_WEIGHT_PERCENT — see that file's
 * header comment for the two scoring-side sources of truth to keep in sync,
 * and for why the 3 scored Discovery dimensions show 40/40/20 (re-normalized
 * across only the scored dimensions) rather than their nominal 20/20/10.
 */
const DIMENSION_ROWS: Array<[string, number | null, string, number]> = [
  ['Discovery Planning Maturity', 40, 'Constitution Maturity', 25],
  ['Problem Framing Quality', 40, 'Artifact Traceability', 25],
  ['Hypothesis Validation Rigor (Coming Soon)', null, 'Supplemental Context', 15],
  ['Solution Option Exploration (Coming Soon)', null, 'Feedback Loop', 15],
  ['User Validation Depth (Coming Soon)', null, 'AI Collaboration', 10],
  ['Discovery-to-Delivery Handoff', 20, 'Process Consistency', 10],
];

/**
 * Standalone "Getting Started" guide — a single static view (no prev/next
 * strip) rendering the requester's full reference PDF verbatim (spec.md
 * v0.2.0). Clones DimensionRubricDialog's Base UI skeleton and chrome
 * (backdrop, popup elevation, header row) rather than extracting a shared
 * dialog primitive out of it (see spec.md Non-goals).
 */
export function GettingStartedDialog({ open, onClose }: GettingStartedDialogProps) {
  return (
    <Dialog.Root
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Backdrop className="getting-started-dialog-backdrop" />
        <Dialog.Popup
          className="getting-started-dialog"
          data-testid="getting-started-dialog"
        >
          <div className="getting-started-dialog__head">
            <Dialog.Title className="getting-started-dialog__title">
              Getting Started
            </Dialog.Title>
            <Dialog.Close className="getting-started-dialog__close" aria-label="Close">
              <XMarkIcon aria-hidden="true" width={20} height={20} />
            </Dialog.Close>
          </div>

          <section className="getting-started-dialog__section">
            <h3 className="getting-started-dialog__heading">What is SPECS-RAY?</h3>
            <p className="getting-started-dialog__body">
              SPECS-RAY scores how your team is practicing spec-driven development —
              once on the <strong>delivery side</strong> (your GitHub Enterprise
              repos) and once on the <strong>discovery side</strong> (your Rally
              Features). The goal isn&apos;t to grade teams; It&apos;s for teams to
              see what features &amp; repos are: Top Performers, Most Improved &amp;
              Newly Adopted. The tool will provide recommendations to improve your
              score, allow easy access to highlighted repos &amp; features, and
              provide anyone the ability to see patterns across the organization.
            </p>
          </section>

          <section className="getting-started-dialog__section">
            <h3 className="getting-started-dialog__heading">How to log in</h3>
            <ol className="getting-started-dialog__list">
              <li>
                Arrive at the Next-Gen Platform Hub (
                <a href="https://ai.coxautoinc.com/" target="_blank" rel="noreferrer">
                  https://ai.coxautoinc.com/
                </a>
                )
              </li>
              <li>Navigate to the SPECS-RAY tile.</li>
            </ol>
          </section>

          <section className="getting-started-dialog__section">
            <h3 className="getting-started-dialog__heading">
              The dashboard in 60 seconds
            </h3>
            <p className="getting-started-dialog__body">
              Once you&apos;re in, here&apos;s what you&apos;re looking at:
            </p>

            <p className="getting-started-dialog__body">
              <strong>Top Pane Features:</strong>
            </p>
            <ul className="getting-started-dialog__list">
              <li>Quick Finder → Search &amp; Recent searches</li>
              <li>Dark/Light mode</li>
              <li>Getting Started Guide</li>
              <li>Logout</li>
            </ul>

            <p className="getting-started-dialog__body">
              <strong>Three Main Tabs:</strong> These three tabs remain consistent
              across each level: CAPTG → Portfolio → Delivery Stream → Release Train →
              Team. Pick yours; everything below filters to that scope.
            </p>
            <h4 className="getting-started-dialog__subheading">Overview</h4>
            <ul className="getting-started-dialog__list">
              <li>Displays the Discovery/Delivery score at that respective level</li>
              <li>
                Displays high-level score by the next level until lowest level (Team)
              </li>
            </ul>
            <h4 className="getting-started-dialog__subheading">Discovery</h4>
            <ul className="getting-started-dialog__list">
              <li>Displays the # Teams, Features &amp; Average Score at that level</li>
              <li>Displays a &apos;Feature Maturity Distribution&apos; view</li>
              <li>Highlights Top Performers, Most Improved &amp; Newly Adopted</li>
              <li>Displays Top 10 Features</li>
              <li>Displays a Leaderboard at that level (exception team level)</li>
            </ul>
            <h4 className="getting-started-dialog__subheading">Delivery</h4>
            <ul className="getting-started-dialog__list">
              <li>Displays the # Teams, Repos &amp; Average Score at that level</li>
              <li>Displays a &apos;Repo Maturity Distribution&apos; view</li>
              <li>Highlights Top Performers, Most Improved &amp; Newly Adopted</li>
              <li>Displays Top 10 Repos</li>
              <li>Displays a Leaderboard at that level (exception team level)</li>
            </ul>

            <p className="getting-started-dialog__body">
              <strong>Features &amp; Repos:</strong> Eventually you will drill down to
              Repos and features showing each dimension&apos;s score, the rubric level,
              and specific findings.
            </p>
            <h4 className="getting-started-dialog__subheading">
              Features (within Discovery Tab)
            </h4>
            <ul className="getting-started-dialog__list">
              <li>
                At the team level, all active features (last 30 days), scores, level,
                classification, recommendation, last activity &amp; last scanned are
                on display.
              </li>
              <li>
                At the feature detail level, The Rally Link is provided, Top
                Recommendations shared, Dimensions used &amp; Score History.
              </li>
              <li>
                Scanning is done automatically every 2 weeks BUT an immediate
                (seconds) team can scan at the feature level if they are eager to see
                if a change improved their score.
              </li>
            </ul>
            <h4 className="getting-started-dialog__subheading">Repos</h4>
            <ul className="getting-started-dialog__list">
              <li>
                At the team level, all active repos (last 30 days), scores, level,
                framework, components linked, recommendation, last activity &amp; last
                scanned are on display.
              </li>
              <li>
                At the repo detail level, The GitHub Link is provided, Top
                Recommendations shared, Dimensions used &amp; Score History.
              </li>
              <li>
                Scanning is done automatically every 2 weeks BUT an immediate
                (seconds) team can scan at the feature level if they are eager to see
                if a change improved their score.
              </li>
            </ul>
          </section>

          <section className="getting-started-dialog__section">
            <h3 className="getting-started-dialog__heading">
              Digging Deeper → Into the Scoring
            </h3>
            <p className="getting-started-dialog__note">
              Note: The tool displays these each dimension and level at time of
              rollout. We anticipate these changing over time so we will remove this
              section from our documentation in the future to avoid drift between tool
              &amp; documentation.
            </p>
            <p className="getting-started-dialog__body">
              <strong>Discovery mode</strong> scores your Rally Features against the
              rubric below, looking at how features are framed, structured, and
              evolved before code is written. Each feature is scored individually and
              rolled up per team.
            </p>
            <p className="getting-started-dialog__body">
              <strong>Delivery mode</strong> scans your team&apos;s GHE repos for
              spec-driven practice signals — agent guardrails, specs, plans, ADRs,
              structured templates, feedback loops.
            </p>
            <p className="getting-started-dialog__body">
              Each feature &amp; repo gets scored on the dimensions below and rolled
              up to an overall score (0–100) and level (L1–L5). Three Discovery
              dimensions are marked <strong>(Coming Soon)</strong> — Rally Features
              don&apos;t yet carry reliable source artifacts for them, so they&apos;re
              excluded from scoring until that changes.
            </p>
            <table className="getting-started-dialog__table">
              <thead>
                <tr>
                  <th scope="col">Discovery Dimension</th>
                  <th scope="col">Weight</th>
                  <th scope="col">Delivery Dimension</th>
                  <th scope="col">Weight</th>
                </tr>
              </thead>
              <tbody>
                {DIMENSION_ROWS.map(([discovery, discoveryWeight, delivery, deliveryWeight]) => (
                  <tr key={discovery}>
                    <td>{discovery}</td>
                    <td>{discoveryWeight === null ? '—' : `${discoveryWeight}%`}</td>
                    <td>{delivery}</td>
                    <td>{deliveryWeight}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="getting-started-dialog__body">
              <strong>The five levels:</strong> L1 Initial | L2 Repeatable | L3
              Defined | L4 Managed | L5 Optimizing
            </p>
            <p className="getting-started-dialog__body">
              <strong>One rule shapes most results:</strong> the overall level
              is capped at your weakest dimension, plus one. A strong team
              with one thin dimension won&apos;t reach the top level on the
              strength of the other five &mdash; that weak dimension is
              usually the first place to look when a score seems lower than
              expected.
            </p>
            <p className="getting-started-dialog__body">
              <strong>A note on Constitution Maturity:</strong> this dimension is
              judged on the practice, not on any one filename — an AI-instructions
              file (e.g. CLAUDE.md or AGENTS.md) and a framework-style
              constitution.md are both &quot;rules artifacts&quot; and score
              equally, whichever convention your team actually uses (including
              both at once). No file type is required or scored higher than
              another; content quality and evolution over time are what move
              the level.
            </p>

            <details className="getting-started-dialog__details">
              <summary className="getting-started-dialog__summary">
                What actually gets read?
              </summary>
              <p className="getting-started-dialog__body">
                <strong>Delivery</strong> reads: rules files your AI tools
                use (e.g. CLAUDE.md or AGENTS.md), root docs like README.md
                and ARCHITECTURE.md, whether specs, plans, or tasks exist and
                link to the code that shipped, and commit messages, PR
                review activity, and follow-up issues &mdash; read as counts
                and patterns, not free text graded line by line. It never
                reads your source code diffs.
              </p>
              <p className="getting-started-dialog__body">
                <strong>Discovery</strong> reads: the Feature&apos;s own
                description, notes, and structure; the parent Epic&apos;s
                description and notes too, not just the Feature on its own;
                and attachments &mdash; but only the ones it can actually
                open: text, markdown, HTML, PDF, and Word documents.{' '}
                <strong>Images are not read</strong> &mdash; an attached
                screenshot or diagram is counted as present, but its
                contents are never opened. It does not read linked Stories,
                Tasks, or Milestones.
              </p>
            </details>

            <details className="getting-started-dialog__details">
              <summary className="getting-started-dialog__summary">
                How is Discovery-to-Delivery Handoff scored?
              </summary>
              <p className="getting-started-dialog__body">
                The AI reads the Feature&apos;s text, the parent
                Epic&apos;s text, and attachment content, then checks how
                much of the &quot;handoff kit&quot; is there: acceptance
                criteria, a scope section, a dependency table, an attached
                mock or wireframe, an architecture or data spec, an
                open-questions log, and success metrics. Having most of
                these reachable lets the dimension reach the top level.
                Higher levels ask for more: proof that feedback actually
                came back and got used, and then proof that the handoff
                process itself keeps improving over time. There&apos;s no
                separate mechanical check &mdash; it&apos;s the AI reading
                the Feature, the Epic, and the attachments together and
                judging what&apos;s actually there.
              </p>
            </details>

            <details className="getting-started-dialog__details">
              <summary className="getting-started-dialog__summary">
                Why does a Delivery score look back 60 days instead of just
                my latest commit?
              </summary>
              <p className="getting-started-dialog__body">
                Two windows are at work here, not one. A repo first has to
                show activity in the last 30 days to be scanned at all
                &mdash; that&apos;s the 30-day number you see on the team
                list. For Delivery repos, once a repo qualifies, the scan
                reviews its last 60 days of commit and PR history, not just
                the latest commit, and checks how much of that recent work
                followed the practice. Most of it did &rarr; the top levels
                are reachable. Some of it did, some didn&apos;t &rarr; the
                level is capped lower. Only a little of it did &rarr;
                capped lower still. Time spent on maintenance-only work
                isn&apos;t held against you. This is why a Delivery score
                can move in either direction over time &mdash; it reflects
                how the repo has been worked on recently, not one single
                point in the past. Discovery features don&apos;t have a
                60-day window like this &mdash; see &ldquo;What actually
                gets read?&rdquo; above for how Discovery scores instead.
              </p>
            </details>
          </section>

          <section className="getting-started-dialog__section">
            <h3 className="getting-started-dialog__heading">
              Digging Deeper → Into Scanning
            </h3>
            <p className="getting-started-dialog__note">
              Note: We plan to scan all features/repos every 2 weeks.
            </p>
            <p className="getting-started-dialog__body">2 things trigger re-scans:</p>
            <ul className="getting-started-dialog__list">
              <li>
                <strong>Delivery:</strong> Rubric changes or any new commit within the
                2-week check.
              </li>
              <li>
                <strong>Discovery:</strong> Rubric changes or any new update/attachment
                within the 2-week check.
              </li>
            </ul>
            <p className="getting-started-dialog__body">
              <strong>Delivery Mode: A note on &quot;SKIPPED&quot; and
              &quot;maintenance-only&quot;:</strong> if a repo&apos;s recent commits
              were all KTLO/maintenance work, the tool flags the scan as SKIPPED
              rather than scoring it. This isn&apos;t a low score — it&apos;s
              &quot;no SDD-relevant work in this window to assess.&quot; Click into
              the repo to see why it was classified that way. Most teams will see at
              least a few SKIPPED rows; that&apos;s expected.
            </p>
          </section>

          <section className="getting-started-dialog__section">
            <h3 className="getting-started-dialog__heading">Feedback Welcome</h3>
            <p className="getting-started-dialog__body">
              Examples of what teams should review and share feedback on:
            </p>
            <ol className="getting-started-dialog__list">
              <li>
                <strong>Score accuracies spot-check.</strong> Does the scores match
                your gut? Where do they line up, and where do they diverge — is it
                the number, the dimension breakdown, or the reasoning behind it?
              </li>
              <li>
                <strong>Coverage.</strong> When you select your team in the header,
                does this look like the right set of repos and Rally Features? Call
                out anything missing or anything showing that shouldn&apos;t be there.
              </li>
              <li>
                <strong>Rubric.</strong> Click into one repo or feature drill-down and
                read the dimension scores and the L1–L5 levels. Does this match how
                you think about SDD maturity for your team? What resonates, what
                feels off, what&apos;s missing?
              </li>
              <li>
                <strong>KTLO / SKIPPED scans (if you see any).</strong> Does the
                SKIPPED vs. scored split match your view of which repos were doing
                real feature work vs. keeping the lights on? Call out anything
                classified right or wrong.
              </li>
              <li>
                <strong>Anything else.</strong> What&apos;s working, what&apos;s
                confusing, what&apos;s broken, what would you want to see that
                isn&apos;t there?
              </li>
            </ol>
            <p className="getting-started-dialog__body">
              We are looking to add a feedback loop within the tool coming soon.
              Thanks for helping shape this!
            </p>
          </section>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
